import { LogManager, Navigation, UrlUtil } from "@mcleod/core";
import {
    ClickEvent, ContextMenuLabelProps, DataHeader, DataSource, DataSourceAction, DataSourceExecutionEvent,
    DataSourceMode
} from "@mcleod/components";
import { CommonDialogs } from "@mcleod/common";
import { AutogenLayoutViewEdiBillingDetail } from "./autogen/AutogenLayoutViewEdiBillingDetail";
import { MovementTabView } from "@mcleod/dispatch/src/MovementTabView";
import { Movement } from "@mcleod/dispatch";
import { EdiBilling } from "./EdiBilling";
import { InvoiceHistoryTabView } from "@mcleod/ar/src/InvoiceHistoryTabView";

const log = LogManager.getLogger("lme.datafusion.EdiBillingDetail");

interface ViewEdiBillingDetailNavigateProps {
    key?: string;
    billingId?: string;
    newTab?: boolean;
    additionalProps?: {};
    styleProps?: {};
}

enum ViewEdiBillingDetailMode {
    EDI_BILLING = "edi_billing",
    MOVEMENT = "movement",
    HISTORY = "history"
}

const layoutURL = "lme/datafusion/ViewEdiBillingDetail";
export class ViewEdiBillingDetail extends AutogenLayoutViewEdiBillingDetail {
    private _activeLayout: EdiBilling | MovementTabView | InvoiceHistoryTabView;
    private mode: DataSourceMode;

    public static navigateTo(props: ViewEdiBillingDetailNavigateProps): void {
        Navigation.navigateTo(
            layoutURL + `?mode=${props.key ? DataSourceMode.UPDATE : DataSourceMode.ADD}`
            + (props.key ? `&key=${props.key}` : "")
            + (props.billingId ? `&billingId=${props.billingId}` : ""),
            { newTab: props.newTab, ...props.styleProps },
            { additionalProps: props.additionalProps }
        );
    }

    override onLoad() {
        this.activeLayout = this.getActiveLayout();
        this.getAdditionalProps();
        this.setupLayouts();
        this.shareNavigateOnClose();
    }

    /**
     * This method ensures that, when navigation instructions have been provided to one of the layouts (edi billing,
     * movements, or invoice history) and not the others, that those navigation instructions are shared with the other layouts.
     * The result of this is that closing the CrudDecorator from any layout will result in the same behavior (unless different
     * navigation instructions have already been assigned to each layout).
     *
     * Note that in a standard CrudDecorator scenario, only one of the layouts will have been assigned navigation
     * instructions (whichever layout is the one that is first visible to the user). Those navigation instructions will
     * be to slide out the CrudDecorator to remove it from the page. So in most cases we expect those standard slide
     * out instructions to simply be applied to the other layouts.
     */
    private shareNavigateOnClose() {
        this.addLayoutLoadListener(() => {
            const ediBillingLayoutHasNavigateOnClose = this.layoutEdiBilling.dataHeader.navigateOnClose != null;
            const movementLayoutHasNavigateOnClose = this.layoutMovementTabView.dataHeader.navigateOnClose != null;
            const invoiceHistoryLayoutHasNavigateOnClose = this.layoutInvoiceHistoryTabView.dataHeader.navigateOnClose != null;

            if ((ediBillingLayoutHasNavigateOnClose || invoiceHistoryLayoutHasNavigateOnClose) && !movementLayoutHasNavigateOnClose)
                this.layoutMovementTabView.dataHeader.navigateOnClose = ediBillingLayoutHasNavigateOnClose ? this.layoutEdiBilling.dataHeader.navigateOnClose : this.layoutInvoiceHistoryTabView.dataHeader.navigateOnClose;
            if ((movementLayoutHasNavigateOnClose || invoiceHistoryLayoutHasNavigateOnClose) && !ediBillingLayoutHasNavigateOnClose)
                this.layoutEdiBilling.dataHeader.navigateOnClose = movementLayoutHasNavigateOnClose ? this.layoutMovementTabView.dataHeader.navigateOnClose : this.layoutInvoiceHistoryTabView.dataHeader.navigateOnClose;
            if ((ediBillingLayoutHasNavigateOnClose || movementLayoutHasNavigateOnClose) && !invoiceHistoryLayoutHasNavigateOnClose)
                this.layoutInvoiceHistoryTabView.dataHeader.navigateOnClose = ediBillingLayoutHasNavigateOnClose ? this.layoutEdiBilling.dataHeader.navigateOnClose : this.layoutMovementTabView.dataHeader.navigateOnClose;
        });
    }

    override getTitleForMode(mode: DataSourceMode): string {
        const urlProps = UrlUtil.getPropsFromUrl(window.location.search);
        return urlProps ? urlProps.orderId ? `Movement - ${urlProps.key}` : `EDI Billing - ${urlProps.key}` : "Add EDI Billing";
    }

    override get mainDataSource(): DataSource {
        return this.getActiveLayout().mainDataSource;
    }

    override setAllDataSourcesToMode(mode: DataSourceMode): void {
        this.getActiveLayout().setAllDataSourcesToMode(mode);
    }

    override get dataHeader(): DataHeader {
        const dh = this.getActiveLayout().dataHeader;
        if (this.getActiveLayout() == this.layoutEdiBilling) {
            if (dh.layout == null) dh.layout = this.layoutEdiBilling;
            this.copyDataHeaderAttributes(dh, this.layoutMovementTabView.dataHeader);
            this.copyDataHeaderAttributes(dh, this.layoutInvoiceHistoryTabView.dataHeader);
        } else if (this.getActiveLayout() == this.layoutMovementTabView) {
            if (dh.layout == null) dh.layout = this.layoutMovementTabView;
            this.copyDataHeaderAttributes(dh, this.layoutEdiBilling.dataHeader);
            this.copyDataHeaderAttributes(dh, this.layoutInvoiceHistoryTabView.dataHeader);
        } else if (this.getActiveLayout() == this.layoutInvoiceHistoryTabView) {
            if (dh.layout == null) dh.layout = this.layoutInvoiceHistoryTabView;
            this.copyDataHeaderAttributes(dh, this.layoutEdiBilling.dataHeader);
            this.copyDataHeaderAttributes(dh, this.layoutMovementTabView.dataHeader);
        }
        this.updateDataHeader(dh);
        return dh;
    }

    public override doAfterDataHeaderSetup(dataHeader: DataHeader): void {
        if (dataHeader.layout != this.activeLayout) {
            dataHeader.layout = this.activeLayout;
        }
        if (this.displayingFromToolbox) {
            dataHeader.showSave = false;
            dataHeader.showSaveAndClose = true;
        }
        this.setDataSourceMode();
    }

    copyDataHeaderAttributes(dh1: DataHeader, dh2: DataHeader) {
        dh2.showClose = dh1.showClose;
        dh2.showSaveAndClose = dh1.showSaveAndClose;
        dh2.onExecute = dh1.onExecute;
        dh2.navigateOnClose = dh1.navigateOnClose;
    }

    updateDataHeader(dataHeader: DataHeader) {
        dataHeader["labelTitle"].visible = false;
        dataHeader.marginBottom = 0;
        if (dataHeader.dataSource == null) {
            dataHeader.dataSource = this.getActiveLayout().mainDataSource;
        }
    }

    setupLayouts() {
        this.setupEdiBillingLayout();
        this.setupMovementLayout();
        this.setupInvoiceHistoryLayout();
    }

    setupEdiBillingLayout() {
        this.layoutEdiBilling.displayingFromToolbox = this.displayingFromToolbox;
        this.layoutEdiBilling.panelMovementTab.addClickListener(async (event: ClickEvent) => {
            this.handleTabChange(ViewEdiBillingDetailMode.MOVEMENT, this.layoutEdiBilling);
            if (this.layoutMovementTabView.layoutMovement.layoutAssignedOverview.visible) {
                await this.layoutMovementTabView.layoutMovement.layoutAssignedOverview.sourceMovementOnDisplay();
            }
            if (this.layoutMovementTabView.layoutMovement.layoutAvailableOverview.visible) {
                await this.layoutMovementTabView.layoutMovement.layoutAvailableOverview.sourceMovementOnDisplay();
            }
        });

        this.layoutEdiBilling.panelInvoiceHistoryTab.addClickListener(async (event: ClickEvent) => {
            this.handleTabChange(ViewEdiBillingDetailMode.HISTORY, this.layoutEdiBilling);
        });

        this.layoutEdiBilling.mainDataSource.addAfterExecutionListener((event: DataSourceExecutionEvent) => {
            this.doAfterExecuteEvent(event, this.layoutMovementTabView);
            // Hide Invoice History tab for inbound records
            this.updateTabVisibilityBasedOnDirection();
        });

        this.layoutEdiBilling.carrierAssignMentChanged = () => {
            if (this.layoutMovementTabView.activeRow) {
                this.layoutMovementTabView.layoutMovement.search({ id: this.layoutMovementTabView.activeRow.get("id") });
            }
        }
    }

    setupMovementLayout() {
        this.layoutMovementTabView.getTitleForMode = () => this.getTitleForMode(this.mode);
        this.layoutMovementTabView.layoutMovement.labelOrderView.text = this.layoutEdiBilling.labelOrderView.text;
        this.layoutMovementTabView.handleOrderTabChange = async () => {
            this.handleTabChange(ViewEdiBillingDetailMode.EDI_BILLING, this.layoutMovementTabView)
            await this.layoutEdiBilling.layoutOverview.sourceOrderOnDisplay();
        };
        this.layoutMovementTabView.handleInvoiceHistoryTabChange = async () => {
            this.handleTabChange(ViewEdiBillingDetailMode.HISTORY, this.layoutMovementTabView);
        };
        this.layoutMovementTabView.movementTabOnChange = () => this.parent.replace(this.parent.components[0], this.dataHeader);
        this.layoutMovementTabView.movementRefreshed = () => {
            if (this.layoutEdiBilling.activeRow) {
                this.layoutEdiBilling.search({ id: this.layoutEdiBilling.activeRow.get("id") });
            }
        };
        this.layoutMovementTabView.activeMovementChanged = (movement: Movement) => {
            if (movement["doAfterExecuteEvent"] == null) {
                movement["doAfterExecuteEvent"] = (event: DataSourceExecutionEvent) => this.doAfterExecuteEvent(event, this.layoutEdiBilling);
                movement.mainDataSource.addAfterExecutionListener(movement["doAfterExecuteEvent"]);
            }
        }
    }

    setupInvoiceHistoryLayout() {
        this.layoutInvoiceHistoryTabView.labelOrderView.text = this.layoutEdiBilling.labelOrderView.text;
        this.layoutInvoiceHistoryTabView.handleOrderTabChange = async () => {
            this.handleTabChange(ViewEdiBillingDetailMode.EDI_BILLING, this.layoutInvoiceHistoryTabView)
            await this.layoutEdiBilling.layoutOverview.sourceOrderOnDisplay();
        }

        this.layoutInvoiceHistoryTabView.handleMovementTabChange = async () => {
            this.handleTabChange(ViewEdiBillingDetailMode.MOVEMENT, this.layoutInvoiceHistoryTabView);
            if (this.layoutMovementTabView.layoutMovement.layoutAssignedOverview.visible) {
                await this.layoutMovementTabView.layoutMovement.layoutAssignedOverview.sourceMovementOnDisplay();
            }
            if (this.layoutMovementTabView.layoutMovement.layoutAvailableOverview.visible) {
                await this.layoutMovementTabView.layoutMovement.layoutAvailableOverview.sourceMovementOnDisplay();
            }
        };

        this.layoutInvoiceHistoryTabView.mainDataSource.addAfterExecutionListener((event: DataSourceExecutionEvent) => {
            this.doAfterExecuteEvent(event, this.layoutMovementTabView);
            this.doAfterExecuteEvent(event, this.layoutEdiBilling);
        });
    }

    /**
     * Updates tab visibility based on the direction field.
     * Hides Invoice History tab for inbound records (direction === "I")
     */
    private updateTabVisibilityBasedOnDirection(): void {
        const direction = this.layoutEdiBilling.mainDataSource?.activeRow?.get("direction");
        const isInbound = direction === "I";

        if (isInbound) {
            // Hide Invoice History tab for inbound records
            if (this.layoutEdiBilling.panelInvoiceHistoryTab) {
                this.layoutEdiBilling.panelInvoiceHistoryTab.visible = false;
            }

            // Remove the tab from the container and adjust borders
            if (this.layoutEdiBilling.panelOrderTabs && this.layoutEdiBilling.panelInvoiceHistoryTab) {
                this.layoutEdiBilling.panelOrderTabs.remove(this.layoutEdiBilling.panelInvoiceHistoryTab, false);
            }

            // Remove border from Movement tab since it's now the last tab
            if (this.layoutEdiBilling.panelMovementTab) {
                this.layoutEdiBilling.panelMovementTab.borderRightWidth = 0;
            }

            // Make the remaining tabs expand to fill the space
            if (this.layoutEdiBilling.panelOrderTab) {
                this.layoutEdiBilling.panelOrderTab.fillRow = true;
            }
            if (this.layoutEdiBilling.panelMovementTab) {
                this.layoutEdiBilling.panelMovementTab.fillRow = true;
            }
        } else {
            // For outbound records, ensure the tab is visible and properly added
            if (this.layoutEdiBilling.panelInvoiceHistoryTab) {
                this.layoutEdiBilling.panelInvoiceHistoryTab.visible = true;

                // Add the tab back to the container if it's not already there
                if (this.layoutEdiBilling.panelOrderTabs && !this.layoutEdiBilling.panelOrderTabs.contains(this.layoutEdiBilling.panelInvoiceHistoryTab)) {
                    this.layoutEdiBilling.panelOrderTabs.add(this.layoutEdiBilling.panelInvoiceHistoryTab);
                }
            }

            // Restore border on Movement tab since Invoice History tab follows it
            if (this.layoutEdiBilling.panelMovementTab) {
                this.layoutEdiBilling.panelMovementTab.borderRightWidth = 1;
            }

            // Reset fillRow properties for normal 3-tab layout
            if (this.layoutEdiBilling.panelOrderTab) {
                this.layoutEdiBilling.panelOrderTab.fillRow = false;
            }
            if (this.layoutEdiBilling.panelMovementTab) {
                this.layoutEdiBilling.panelMovementTab.fillRow = false;
            }
        }
    }    setDataSourceMode() {
        const urlProps = UrlUtil.getPropsFromUrl(window.location.search);
        if (this.displayingFromToolbox || urlProps == null || urlProps?.mode == "add" || this.dataHeader.parent["mode"] == DataSourceMode.ADD) {
            this.mode = DataSourceMode.ADD;
            this.layoutEdiBilling.panelMovementTab.visible = false;
            this.layoutEdiBilling.panelInvoiceHistoryTab.visible = false;
            this.layoutEdiBilling.panelOrderTab.borderRightWidth = 0;
            this.layoutEdiBilling.labelOrderView.text = "Add EDI Billing";
            this.layoutEdiBilling.labelStatusBadge.visible = false;
        } else {
            this.mode = DataSourceMode.UPDATE;
        }
    }

    getAdditionalProps() {
        const additionalProps = this["additionalProps"];
        if (additionalProps) {
            for (const prop in additionalProps) {
                if (this.activeLayout instanceof MovementTabView) {
                    this.activeLayout.layoutMovement[prop] = additionalProps[prop];
                } else {
                    this.activeLayout[prop] = additionalProps[prop];
                }
            }
        }
    }

    getActiveLayout() {
        let activeLayout: EdiBilling | MovementTabView | InvoiceHistoryTabView = this.activeLayout;
        if (this.activeLayout == null) {
            activeLayout = this.layoutEdiBilling;
            activeLayout.hideTitle = false;
            if (!this.displayingFromToolbox) {
                const urlProps = UrlUtil.getPropsFromUrl(window.location.search);
                if (urlProps?.orderId) {
                    activeLayout = this.layoutMovementTabView;
                }
            }
        }
        return activeLayout;
    }

    get activeLayout(): EdiBilling | MovementTabView | InvoiceHistoryTabView {
        return this._activeLayout;
    }

    set activeLayout(layout: EdiBilling | MovementTabView | InvoiceHistoryTabView) {
        this._activeLayout = layout;
        this._activeLayout.visible = true;
    }

    doAfterExecuteEvent(event: DataSourceExecutionEvent, layoutToRefresh: EdiBilling | MovementTabView | InvoiceHistoryTabView) {
        if (event.getAction() == DataSourceAction.UPDATE) {
            if (layoutToRefresh instanceof EdiBilling) {
                // Post moves that have changed data
                const changedMovement = this.layoutMovementTabView.getChangedMovement();
                if (changedMovement != null) {
                    changedMovement.mainDataSource.post();
                    return;
                }
            }
            if (layoutToRefresh.activeRow) {
                if (layoutToRefresh instanceof MovementTabView) {
                    layoutToRefresh.layoutMovementTabs.currentTabId = null;
                }
                layoutToRefresh.search({ id: layoutToRefresh.activeRow.get("id") });
            }
        }
    }

    public static addNavigationContextMenuItems(itemProps: Partial<ContextMenuLabelProps>[], billingId: string): Partial<ContextMenuLabelProps>[] {
        if (itemProps != null)  {
            const navigationItems = ViewEdiBillingDetail.createNavigationContextMenuItems(billingId);
            if (itemProps.length > 0 && navigationItems.length > 0) {
                const allVisibleItems = itemProps.filter(item => item.visible);
                const lastVisibleItem = allVisibleItems[allVisibleItems.length - 1];
                if (lastVisibleItem) lastVisibleItem.separatorAfter = true;
                itemProps.push(...navigationItems);
            }
        }
        return itemProps;
    }

    public static createNavigationContextMenuItems(billingId: string): Partial<ContextMenuLabelProps>[] {
        if (billingId == null)  return [];
        return [
            {
                text: "Open in New Tab",
                onClick: () => ViewEdiBillingDetail.navigateTo({
                    key: billingId,
                    newTab: true
                })
            },
            {
                text: "Open in New Window",
                onClick: () => ViewEdiBillingDetail.navigateTo({
                    key: billingId,
                    newTab: true,
                    styleProps: { windowDecorators: false }
                })
            }
        ]
    }

    async handleTabChange(viewMode: ViewEdiBillingDetailMode, layout: EdiBilling | MovementTabView | InvoiceHistoryTabView): Promise<void> {
        if (!this.activeLayout || !this.activeLayout.activeRow) return;

        // Prevent switching to Invoice History tab for inbound records
        if (viewMode === ViewEdiBillingDetailMode.HISTORY) {
            const direction = this.layoutEdiBilling.mainDataSource?.activeRow?.get("direction");
            const isInbound = direction === "I";
            if (isInbound) {
                log.debug("Cannot switch to Invoice History tab for inbound records");
                return;
            }
        }

        let cachedBillingId: string | undefined;
        let cachedOrderId: string | undefined;

        if(this.layoutEdiBilling?.sourceEdiBillingDetails?.activeRow) {
            cachedOrderId = this.layoutEdiBilling?.sourceEdiBillingDetails?.activeRow?.get?.("order_id");
            cachedBillingId = this.layoutEdiBilling?.sourceEdiBillingDetails?.activeRow?.get?.("id");
        } else if (this.layoutMovementTabView?.activeRow) {
            cachedOrderId = this.layoutMovementTabView?.activeRow?.get?.("orders.id");
        }

        // Handle unsaved changes
        if (this.activeLayout.mainDataSource?.hasChanged(true)) {
            try {
                const saveChanges = await CommonDialogs.showYesNo(
                    "Save your changes on this tab before switching?",
                    "Save Changes"
                );

                if (saveChanges === undefined) return;
                if (saveChanges) {
                    await this.activeLayout.mainDataSource.post();
                }
            } catch (error) {
                log.debug("Error while saving changes:", error);
                return;
            }
        }

        const oldDataHeader = this.dataHeader;
        let id: string | undefined;

        try {
            const searchId = this.setActiveLayoutAndId(viewMode, layout);
            await this.searchData(searchId);
        } catch (error) {
            log.debug("Tab switch failed:", error);
            return;
        }

        this.parent?.replace?.(oldDataHeader, this.dataHeader);

        // Ensure navigation context is preserved after tab switch
        this.shareNavigateOnCloseImmediate();

        // Preserve the original EDI Billing label text across all layouts
        this.preserveOrderViewLabelText();
    }

    /**
     * Immediately share navigate on close context between all layouts
     */
    private shareNavigateOnCloseImmediate() {
        const ediBillingLayoutHasNavigateOnClose = this.layoutEdiBilling.dataHeader.navigateOnClose != null;
        const movementLayoutHasNavigateOnClose = this.layoutMovementTabView.dataHeader.navigateOnClose != null;
        const invoiceHistoryLayoutHasNavigateOnClose = this.layoutInvoiceHistoryTabView.dataHeader.navigateOnClose != null;

        if ((ediBillingLayoutHasNavigateOnClose || invoiceHistoryLayoutHasNavigateOnClose) && !movementLayoutHasNavigateOnClose)
            this.layoutMovementTabView.dataHeader.navigateOnClose = ediBillingLayoutHasNavigateOnClose ? this.layoutEdiBilling.dataHeader.navigateOnClose : this.layoutInvoiceHistoryTabView.dataHeader.navigateOnClose;
        if ((movementLayoutHasNavigateOnClose || invoiceHistoryLayoutHasNavigateOnClose) && !ediBillingLayoutHasNavigateOnClose)
            this.layoutEdiBilling.dataHeader.navigateOnClose = movementLayoutHasNavigateOnClose ? this.layoutMovementTabView.dataHeader.navigateOnClose : this.layoutInvoiceHistoryTabView.dataHeader.navigateOnClose;
        if ((ediBillingLayoutHasNavigateOnClose || movementLayoutHasNavigateOnClose) && !invoiceHistoryLayoutHasNavigateOnClose)
            this.layoutInvoiceHistoryTabView.dataHeader.navigateOnClose = ediBillingLayoutHasNavigateOnClose ? this.layoutEdiBilling.dataHeader.navigateOnClose : this.layoutMovementTabView.dataHeader.navigateOnClose;

        // Also share showClose flag
        const anyLayoutHasShowClose = this.layoutEdiBilling.dataHeader.showClose || this.layoutMovementTabView.dataHeader.showClose || this.layoutInvoiceHistoryTabView.dataHeader.showClose;
        if (anyLayoutHasShowClose) {
            this.layoutEdiBilling.dataHeader.showClose = true;
            this.layoutMovementTabView.dataHeader.showClose = true;
            this.layoutInvoiceHistoryTabView.dataHeader.showClose = true;
        }
    }

    /**
     * Preserve the original EDI Billing label text across all layouts to prevent
     * unwanted renaming when switching between tabs
     */
    private preserveOrderViewLabelText() {
        const originalText = "DataFusion Invoice View";

        // Ensure all layouts maintain the correct label text
        if (this.layoutEdiBilling.labelOrderView.text !== "Add EDI Billing") {
            this.layoutEdiBilling.labelOrderView.text = originalText;
        }
        this.layoutMovementTabView.layoutMovement.labelOrderView.text = this.layoutEdiBilling.labelOrderView.text;
        this.layoutInvoiceHistoryTabView.labelOrderView.text = this.layoutEdiBilling.labelOrderView.text;
    }

    setActiveLayoutAndId(viewMode: ViewEdiBillingDetailMode, layout: EdiBilling | MovementTabView | InvoiceHistoryTabView): string {
        let id: string;

        // Set all layouts to not visible first
        this.setLayoutsToNotVisible();

        // Then set the active layout and determine the appropriate ID
        switch (viewMode) {
            case ViewEdiBillingDetailMode.MOVEMENT:
                this.activeLayout = this.layoutMovementTabView;
                id = layout instanceof EdiBilling ?
                    this.layoutEdiBilling.sourceOrders?.activeRow?.get("curr_movement_id") :
                    this.layoutInvoiceHistoryTabView.activeRow?.get("curr_movement_id");
                break;

            case ViewEdiBillingDetailMode.EDI_BILLING:
                this.activeLayout = this.layoutEdiBilling;
                id = layout instanceof MovementTabView ?
                    this.layoutMovementTabView.activeRow?.get("orders.id") :
                    this.layoutInvoiceHistoryTabView.activeRow?.get("id");
                break;

            case ViewEdiBillingDetailMode.HISTORY:
                this.activeLayout = this.layoutInvoiceHistoryTabView;
                if (layout instanceof EdiBilling) {
                    id = this.layoutEdiBilling.sourceEdiBillingDetails?.activeRow?.get("order_id");
                } else {
                    id = this.layoutMovementTabView.activeRow?.get("orders.id");
                }
                break;
        }

        // Make sure we never return undefined/null
        return id || '';
    }

    async searchData(id: string) {
        if (!id) {
            return; // Don't attempt to search with an empty ID
        }

        if (!this.activeLayout.activeRow) {
            await this.activeLayout.search({ id });
            if (this.activeLayout instanceof InvoiceHistoryTabView) {
                await this.activeLayout.sourceOpenItem.search({ order_id_input: id });
            }
            this.activeLayout.mainDataSource.mode = this.mode;
        }
    }

    setLayoutsToNotVisible() {
        this.layoutEdiBilling.visible = false;
        this.layoutMovementTabView.visible = false;
        this.layoutInvoiceHistoryTabView.visible = false;
    }
}
